import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/Login";
import { Signup } from "@/pages/Signup";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { AuthCallback } from "@/pages/AuthCallback";
import { CandidateDashboard } from "@/pages/CandidateDashboard";
import { ResumePage } from "@/pages/ResumePage";
import { JobsList } from "@/pages/JobsList";
import { JobDetail } from "@/pages/JobDetail";
import { MyApplications } from "@/pages/MyApplications";
import { RecruiterDashboard } from "@/pages/RecruiterDashboard";
import { PostJob } from "@/pages/PostJob";
import { JobApplicants } from "@/pages/JobApplicants";
import { TeamPage } from "@/pages/TeamPage";
import { BlogList } from "@/pages/BlogList";
import { BlogPost } from "@/pages/BlogPost";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { AdminQuotes } from "@/pages/AdminQuotes";

const Shell = () => (
  <div className="min-h-screen bg-[#0B0F19] text-slate-100">
    <Navbar />
    <Outlet />
  </div>
);

const Protected = ({ role, roles }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  const allowed = roles || (role ? [role] : null);
  // Admin has access to all authenticated routes.
  if (allowed && user.role !== "admin" && !allowed.includes(user.role)) {
    const home = user.role === "recruiter" ? "/recruiter" : "/candidate";
    return <Navigate to={home} replace />;
  }
  return <Outlet />;
};

const Router = () => {
  const loc = useLocation();
  // Handle Emergent auth callback synchronously during render
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Landing />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route element={<Protected role="candidate" />}>
          <Route path="/candidate" element={<CandidateDashboard />} />
          <Route path="/candidate/resume" element={<ResumePage />} />
          <Route path="/candidate/applications" element={<MyApplications />} />
          <Route path="/jobs" element={<JobsList />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
        </Route>

        <Route element={<Protected role="recruiter" />}>
          <Route path="/recruiter" element={<RecruiterDashboard />} />
          <Route path="/recruiter/team" element={<TeamPage />} />
          <Route path="/recruiter/post" element={<PostJob />} />
          <Route path="/recruiter/jobs/:id" element={<JobApplicants />} />
          <Route path="/admin/quotes" element={<AdminQuotes />} />
        </Route>

        <Route element={<Protected roles={["admin"]} />}>
          <Route path="/admin" element={<AdminDashboard />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Router />
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
